<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { CSAT_RATINGS } from 'shared/constants/messages';

const props = defineProps({
  ratingPercentage: {
    type: Object,
    default: () => ({}),
  },
  ratingCount: {
    type: Object,
    default: () => ({}),
  },
  totalResponseCount: {
    type: Number,
    default: 0,
  },
  totalSentMessagesCount: {
    type: Number,
    default: 0,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const sortedRatings = computed(() =>
  [...CSAT_RATINGS].sort((a, b) => b.value - a.value)
);

const formatPercent = value => (value ? `${value}%` : '0%');
const formatCount = value =>
  `${t('COMMON.LEFT_PAREN')}${value}${t('COMMON.RIGHT_PAREN')}`;

const getRatingLabel = value => {
  const rating = CSAT_RATINGS.find(r => r.value === value);
  if (!rating) return '';

  switch (rating.value) {
    case 1:
      return t('CSAT.RATINGS.POOR');
    case 2:
      return t('CSAT.RATINGS.FAIR');
    case 3:
      return t('CSAT.RATINGS.AVERAGE');
    case 4:
      return t('CSAT.RATINGS.GOOD');
    case 5:
      return t('CSAT.RATINGS.EXCELLENT');
    default:
      return '';
  }
};

const getRatingCount = value => {
  return props.ratingCount[value] || 0;
};

const notAnsweredCount = computed(() => {
  const count = props.totalSentMessagesCount - props.totalResponseCount;
  return count > 0 ? count : 0;
});

const notAnsweredPercentage = computed(() => {
  if (props.totalSentMessagesCount === 0) return 0;
  return (
    (notAnsweredCount.value / props.totalSentMessagesCount) *
    100
  ).toFixed(2);
});
</script>

<template>
  <div
    class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 px-6 py-5"
  >
    <span class="text-sm font-medium text-n-slate-11">
      {{ $t('CSAT_REPORTS.METRIC.RATING_DISTRIBUTION') }}
    </span>

    <div v-if="isLoading" class="mt-4">
      <div class="h-6 w-full rounded-full bg-n-slate-3 animate-pulse" />
      <div class="flex gap-6 mt-4">
        <div
          v-for="n in 5"
          :key="n"
          class="h-4 w-20 rounded bg-n-slate-3 animate-pulse"
        />
      </div>
    </div>

    <div v-else class="mt-4">
      <div
        v-if="totalResponseCount"
        class="flex h-6 w-full rounded-full overflow-hidden bg-n-alpha-2"
      >
        <div
          v-for="rating in sortedRatings"
          :key="rating.value"
          v-tooltip="
            `${getRatingLabel(rating.value)}: ${formatPercent(ratingPercentage[rating.value])} (${getRatingCount(rating.value)})`
          "
          :style="{
            width: `${ratingPercentage[rating.value]}%`,
            backgroundColor: rating.color,
          }"
          class="h-full transition-all duration-300 first:rounded-s-full last:rounded-e-full cursor-default"
        />
      </div>
      <div v-else class="h-6 w-full rounded-full bg-n-alpha-2" />

      <div class="mt-4 flex flex-col gap-3">
        <div
          class="flex flex-wrap justify-between items-center w-full gap-y-2 gap-x-2"
        >
          <div
            v-for="rating in sortedRatings"
            :key="rating.value"
            class="flex items-center gap-1.5"
          >
            <span class="text-sm text-n-slate-11">
              {{ getRatingLabel(rating.value) }}
            </span>
            <span class="text-sm font-medium text-n-slate-12">
              {{ formatPercent(ratingPercentage[rating.value]) }}
            </span>
            <span class="text-xs text-n-slate-10">
              {{ formatCount(getRatingCount(rating.value)) }}
            </span>
          </div>
        </div>
        <div class="flex justify-center items-center gap-1.5 w-full mt-1">
          <span class="text-sm text-n-slate-11">{{
            $t('CSAT.NOT_ANSWERED')
          }}</span>
          <span class="text-sm font-medium text-n-slate-12">
            {{ formatPercent(notAnsweredPercentage) }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{ formatCount(notAnsweredCount) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
